import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class SourcingInputAppBasketService {

  constructor(private http: HttpClient) { }

  /**
   * Get Apportionment Bases
   * Action code: oslque (Action id 30128)
   * Legacy: POST body = workspaceFactory.activeScreen.filters.getFilterParams()
   */
  getAppBases(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    _rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=oslque`;
    return this.postFormEncoded(url, this.filterParamsPayload(filterParams, globalService));
  }

  /**
   * Get Baskets
   * Action code: xm5oxg (Action id 30128)
   * Legacy: POST body = getFilterParams() (full screen filter object)
   */
  getBaskets(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    _rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=xm5oxg`;
    return this.postFormEncoded(url, this.filterParamsPayload(filterParams, globalService));
  }

  /**
   * Get Existing Baskets
   * Action code: geqc7n (Action id 32664)
   */
  getExistingBaskets(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=geqc7n`;
    const params: any = {
      tax_year: filterParams?.tax_year || globalService?.tax_year || '',
      scenario: filterParams?.scenario || globalService?.scenario || '74',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || '250',
      combination_key: rowData?.COMBINATION_KEY || '',
      acct_key: this.resolveAcctKey(rowData),
      tp_key: this.resolveTpKey(rowData),
      ho_combination_key: rowData?.HO_COMBINATION_KEY || ''
    };
    return this.postFormEncoded(url, params);
  }

  /**
   * Get Issue ID
   * Action code: 8r4vs8 (Action id 32666)
   */
  getIssueId(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    userSettings: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=8r4vs8`;
    const params: any = {
      sso_id: userSettings?.user?.sso_id || '',
      issue_tax_year: filterParams?.tax_year || globalService?.tax_year || '',
      issue_scenario: filterParams?.scenario || globalService?.scenario || '74'
    };
    return this.postFormEncoded(url, params);
  }

  /**
   * Get App Base Issue Key
   * Action code: ne26e4 (Action id 32696)
   */
  getAppBaseIssueKey(
    baseUrl: string,
    filterParams: any,
    globalService: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=ne26e4`;
    const params: any = {
      tax_year: filterParams?.tax_year || globalService?.tax_year || '',
      scenario: filterParams?.scenario || globalService?.scenario || '74',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || '250',
      acct_key: this.resolveAcctKey(rowData),
      tp_key: this.resolveTpKey(rowData),
      base_id: rowData?.BASE_ID ?? ''
    };
    return this.postFormEncoded(url, params);
  }

  /**
   * Save Apportionment Base
   * Action code: l49zjo (HO) or ftveh4 (QBU) (Action id 30129)
   */
  saveApportionmentBase(
    baseUrl: string,
    ssoId: string,
    jsonSettings: any,
    jsonObj: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const saveUrl = (rowData.GRID_TYPE && rowData.GRID_TYPE === 'QBU') ?
      `${baseUrl}/saveJsonObject?action_code=ftveh4` :
      `${baseUrl}/saveJsonObject?action_code=l49zjo`;

    const checkLockedbj = [{
      consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.FILING_KEY || filterParams?.filing_key || globalService?.filing_key || '1',
      scenario_key: parseInt(filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '74', 10)
    }];

    const params: any = {
      tax_year: filterParams?.tax_year || globalService?.tax_year || rowData?.TAX_YEAR || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || rowData?.SCREEN_KEY || '',
      scenario: filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '74',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || rowData?.JCD_KEY || '250',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || rowData?.JCD_TA_KEY || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || rowData?.ACTIVITY_KEY || '',
      screen_type: filterParams?.screen_type || globalService?.screen_type || 'grid-dynamic',
      is_locked: filterParams?.is_locked || globalService?.is_locked || 'N',
      issue_id: filterParams?.issue_id || globalService?.issue_id || rowData?.ISSUE_ID || '',
      issue_short_desc: filterParams?.issue_short_desc || globalService?.issue_short_desc || rowData?.ISSUE_SHORT_DESC || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      checkLockedbj: JSON.stringify(checkLockedbj),
      scenarioCode: filterParams?.scenarioCode || filterParams?.scenario_code || globalService?.scenarioCode || 'RAF',
      is_issue_key_enabled: filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || 'N',
      sso_id: ssoId,
      process_name: 'SAVE_GHOST_APPORTIONMENT_BASE',
      jsonSettings: JSON.stringify(jsonSettings),
      jsonObj: JSON.stringify(jsonObj),
      filing_key: filterParams?.filing_key || globalService?.filing_key || rowData?.FILING_KEY || rowData?.CONSOL_GROUP_KEY || '1',
      fir_ho_leid: filterParams?.fir_ho_leid || globalService?.fir_ho_leid || rowData?.FIR_HO_LEID || rowData?.HO_COMBINATION_KEY || rowData?.LEID || ''
    };

    return this.postFormEncoded(saveUrl, params);
  }

  /**
   * Save Ghost Sourcing Basket Data
   * Action code: 7s7kpw (HO) or qwwxtm (QBU) (Action id 1230)
   */
  saveGhostSourcingBasketData(
    baseUrl: string,
    json_arrOBJ: any[],
    settings: any,
    globalService: any,
    filterParams: any,
    rowData: any
  ): Observable<any> {
    const saveUrl = (rowData.GRID_TYPE && rowData.GRID_TYPE === 'QBU') ?
      `${baseUrl}/saveJsonObject?action_code=qwwxtm` :
      `${baseUrl}/saveJsonObject?action_code=7s7kpw`;

    const checkLockedbj = [{
      leid: rowData.LEID || '',
      me_code: rowData.ME_CODE || '',
      cdr_number: rowData.CDR_NO || rowData.CDR_NUMBER || '',
      consol_group_key: rowData.CONSOL_GROUP_KEY || rowData.FILING_KEY || filterParams?.filing_key || globalService?.filing_key || '1',
      reporting_period: rowData.REPORTING_PERIOD || 1,
      combination_key: rowData.COMBINATION_KEY || '',
      scenario_key: parseInt(filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '74', 10)
    }];

    const params: any = {
      jsonSettings: JSON.stringify(settings),
      jsonObj: JSON.stringify(json_arrOBJ),
      scenarioCode: filterParams?.scenarioCode || filterParams?.scenario_code || globalService?.scenarioCode || 'RAF',
      is_issue_key_enabled: filterParams?.is_issue_key_enabled || globalService?.is_issue_key_enabled || 'N',
      tax_year: filterParams?.tax_year || globalService?.tax_year || rowData?.TAX_YEAR || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || rowData?.SCREEN_KEY || '',
      scenario: filterParams?.scenario || globalService?.scenario || rowData?.SCENARIO || '74',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || rowData?.JCD_KEY || '250',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || rowData?.JCD_TA_KEY || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || rowData?.ACTIVITY_KEY || '',
      screen_type: filterParams?.screen_type || globalService?.screen_type || 'grid-dynamic',
      is_locked: filterParams?.is_locked || globalService?.is_locked || 'N',
      issue_id: filterParams?.issue_id || globalService?.issue_id || rowData?.ISSUE_ID || '',
      issue_short_desc: filterParams?.issue_short_desc || globalService?.issue_short_desc || rowData?.ISSUE_SHORT_DESC || '',
      issue_id_for_tx_save: filterParams?.issue_id_for_tx_save || globalService?.issue_id_for_tx_save || rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      checkLockedbj: JSON.stringify(checkLockedbj),
      filing_key: filterParams?.filing_key || globalService?.filing_key || rowData?.FILING_KEY || rowData?.CONSOL_GROUP_KEY || '1',
      fir_ho_leid: filterParams?.fir_ho_leid || globalService?.fir_ho_leid || rowData?.FIR_HO_LEID || rowData?.HO_COMBINATION_KEY || rowData?.LEID || '',
      process_name: 'SAVE_GHOST_BASKET_DATA'
    };

    return this.postFormEncoded(saveUrl, params);
  }

  /** Legacy getFilterParams() payload for getAppBases / getBaskets. */
  private filterParamsPayload(filterParams: any, globalService: any): any {
    const fp = filterParams && typeof filterParams === 'object' ? { ...filterParams } : {};
    const gs = globalService && typeof globalService === 'object' ? globalService : {};
    return _.merge({}, gs, fp);
  }

  /** Legacy: basketParam.TP_KEY — preserve 0; accept tp_key alias from grid payload. */
  private resolveTpKey(rowData: any): string | number {
    const v = rowData?.TP_KEY ?? rowData?.tp_key;
    if (v === undefined || v === null || v === '') {
      return '';
    }
    return v;
  }

  /** Legacy: GHOST_ACCT when RECORD_ID is GHOST, else LOCAL_ACCT. */
  private resolveAcctKey(rowData: any): string {
    if (!rowData) {
      return '';
    }
    if (rowData.RECORD_ID === 'GHOST') {
      return rowData.GHOST_ACCT != null && rowData.GHOST_ACCT !== ''
        ? rowData.GHOST_ACCT.toString()
        : '';
    }
    if (rowData.LOCAL_ACCT != null && rowData.LOCAL_ACCT !== '') {
      return rowData.LOCAL_ACCT.toString();
    }
    if (rowData.GHOST_ACCT != null && rowData.GHOST_ACCT !== '') {
      return rowData.GHOST_ACCT.toString();
    }
    return '';
  }

  private postFormEncoded(url: string, params: Record<string, any>): Observable<any> {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        urlEncoded.set(key, value.toString());
      }
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      withCredentials: true
    };

    return this.http.post<any>(url, urlEncoded.toString(), httpOptions);
  }
}
